// src/pages/FriendList.jsx
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Input,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DataTable from 'react-data-table-component';
import { ChatApi } from '../../services/AllServices';
import errorHandler from '../../utils/errorHandler';
import { IMAGE_BASE } from '../../utils/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '../../components/CustomLoader';
import { TrimText } from '../../utils/helpers';
import ChatDialog from './ChatView';
import BackButton from '../../components/Buttons/BackButton';

export default function FriendList() {
    const navigate = useNavigate();
    const { id: userId } = useParams(); // logged-in user ID
    const [searchItem, setSearchItem] = useState('');
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [loaderDelay, setLoaderDelay] = useState(false);

    const [openChat, setOpenChat] = useState(false);
    const [selectedFriend, setSelectedFriend] = useState({ id: null, name: '' });
    const [messages, setMessages] = useState([]);

    // Fetch list of chat persons
    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            const res = await ChatApi.getChatPersonsList({ userId });
            if (res.status === 200) {
                setData(res.data.data.records || res.data.data); // adapt as per API response
                setTotalPages(res.data.data.totalRecords || res.data.data.length);
            }
            setLoaderDelay(true);
        } catch (err) {
            errorHandler(err);
            setLoaderDelay(false);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedSearch = useCallback(
        debounce(() => fetchUsers(), 400),
        [searchItem, currentPage, pageSize]
    );

    useEffect(() => {
        debouncedSearch();
    }, [searchItem, currentPage, pageSize]);

    // Fetch chat messages for a friend
    const fetchChatMessages = async (friendId) => {
        try {
            const res = await ChatApi.getChatHistory({ userId, friendId });
            if (res.status === 200) {
                setMessages(res.data.data || []);
            }
        } catch (err) {
            errorHandler(err);
        }
    };

    // Delete a single message
    const handleDeleteMessage = async (messageId) => {
        try {
            const res = await ChatApi.deleteMessage({ id: messageId });
            if (res.status === 200) {
                setMessages((prev) => prev.filter((msg) => msg._id !== messageId));
            }
        } catch (err) {
            errorHandler(err);
        }
    };

    // Clear chat history
    const handleClearHistory = async (userId, friendId) => {
        try {
            const res = await ChatApi.deleteChat({ userId, friendId });
            if (res.status === 200) {
                setMessages([]);
            }
        } catch (err) {
            errorHandler(err);
        }
    };

    const columns = [
        {
            name: 'S.No.',
            cell: (row, idx) => (currentPage - 1) * pageSize + idx + 1,
            width: '100px',
        },
        {
            name: 'User Name',
            selector: (row) => TrimText(row.username, 25),
            sortable: true,
        },
        {
            name: 'Name',
            selector: (row) => `${row.firstname} ${row.lastname}`,
            sortable: true,
        },
        {
            name: 'Email',
            selector: (row) => row.email,
            sortable: true,
        },
        {
            name: 'Avatar',
            cell: (row) => (
                <img
                    src={`${IMAGE_BASE}${row.image}`}
                    alt="avatar"
                    style={{ width: 40, height: 40, borderRadius: '50%' }}
                />
            ),
            width: '80px',
        },
        {
            name: 'Action',
            right: true,
            cell: (row) => (
                <Tooltip title="View Chat">
                    <IconButton
                        color="primary"
                        onClick={() => {
                            setSelectedFriend({
                                id: row._id,
                                name: `${row.firstname} ${row.lastname}` || row.username,
                            });
                            fetchChatMessages(row._id);
                            setOpenChat(true);
                        }}

                    >
                        <VisibilityIcon />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <>
            <Card>
                <CardHeader title={<BackButton variant="h6" />} />
                <CardHeader title="Friend List" />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                    <Input
                        value={searchItem}
                        placeholder="Search"
                        onChange={(e) => {
                            setSearchItem(e.target.value);
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => {
                                        setSearchItem('');
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </Box>
                <CardContent>
                    <DataTable
                        columns={columns}
                        data={data}
                        pagination
                        paginationServer
                        paginationTotalRows={totalPages}
                        onChangePage={(page) => setCurrentPage(page)}
                        paginationPerPage={pageSize}
                        onChangeRowsPerPage={(r) => setPageSize(r)}
                        paginationRowsPerPageOptions={[10, 20, 50]}
                        progressPending={isLoading || !loaderDelay}
                        progressComponent={<Loader />}
                    />
                </CardContent>
            </Card>

            {/* Chat dialog */}
            <ChatDialog
                open={openChat}
                onClose={() => setOpenChat(false)}
                messages={messages}
                userId={userId}
                friendId={selectedFriend.id}
                friendName={selectedFriend.name}
                onDeleteMessage={handleDeleteMessage}
                onClearHistory={handleClearHistory}
            />

        </>
    );
}
